import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { colors, FONT, radii } from '../../../../theme';
import { S } from '../../dashboardStyles';
import { SS } from '../settingsStyles';
import SettingsScreen from '../SettingsScreen';
import { AdminFilterSheet, AdminSortSheet } from './AdminSheets';
import UndoToast from '../UndoToast';
import { api } from '../../../../services/api';

const STATUS_LABEL = {
  all: 'Все', online: 'В сети', never: 'Не заходили ни разу',
  dormant90: 'Спят >90 дней', disabled: 'Заблокированы', admins: 'Администраторы',
};
const SORT_LABEL = { created_desc: 'сначала новые', last_seen_desc: 'по входу', name_asc: 'по имени' };

/**
 * The user list, for a hundred people.
 *
 * Three decisions worth stating, because each of them is the opposite of the
 * obvious one:
 *
 * - The row has no destructive action and no swipe. Deleting used to be a
 *   ~18px text button inside a scrolling list, which is the smallest target in
 *   the app attached to the least reversible operation in it.
 * - No green presence dots. Presence is a snapshot of sockets held in the
 *   server's memory; a dot that never updates lies quietly for the rest of the
 *   afternoon. The list says "в сети" in text and stamps the header with the
 *   time it asked.
 * - Pages come with an explicit button rather than infinite scroll, which
 *   fights pull-to-refresh and loses the position when coming back from a card.
 */
export default function AdminUsersScreen({ onBack, onOpenUser, currentUserId }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [cursor, setCursor] = useState(null);
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('created_desc');
  const [q, setQ] = useState('');
  const [asOf, setAsOf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sheet, setSheet] = useState(null);
  const [creds, setCreds] = useState(null);
  // Selection exists for one pair of operations, both reversible. There is no
  // bulk delete: nothing that irreversible should be reachable by a checkbox.
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [undo, setUndo] = useState(null);
  const debounce = useRef(null);

  const load = useCallback(async (opts = {}) => {
    setBusy(true); setError('');
    try {
      const r = await api.adminGetUsers({
        limit: 50, status, sort,
        ...(q ? { q } : {}),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
      });
      setItems(prev => (opts.cursor ? [...prev, ...(r.items || [])] : (r.items || [])));
      setCounts(r.counts || {});
      setCursor(r.next_cursor || null);
      setAsOf(r.as_of ? new Date(r.as_of) : new Date());
    } catch (e) {
      setError(e.message || 'Не удалось загрузить список');
    } finally {
      setBusy(false);
    }
  }, [status, sort, q]);

  useEffect(() => { load(); }, [status, sort]); // eslint-disable-line

  // Typing hits the server, so it waits for a pause rather than a keystroke.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(), 250);
    return () => clearTimeout(debounce.current);
  }, [q]); // eslint-disable-line

  const act = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); await load(); }
    catch (e) { setError(e.message || 'Не получилось'); }
    finally { setBusy(false); }
  };

  /**
   * Block or unblock everything selected, in one request.
   *
   * Blocking offers a way back for a few seconds, because it is reversible and
   * the mistake is easiest to notice right here.
   */
  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    await act(() => api.adminBulk(action, ids));
    setSelected(new Set());
    setSelecting(false);
    if (action === 'disable') {
      setUndo({ ids, text: ids.length === 1 ? 'Пользователь заблокирован' : `Заблокировано: ${ids.length}` });
    }
  };

  const create = async () => {
    setError('');
    try {
      const u = await api.adminCreateUser({});
      setCreds(u);
      load();
    } catch (e) { setError(e.message || 'Не удалось создать'); }
  };

  return (
    <SettingsScreen title="Пользователи" onBack={onBack} scrollKey="admin-users">
      <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} color={colors.gray400}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Имя, заметка, логин или номер"
            style={{ ...S.input, width: '100%', paddingLeft: 36, minHeight: 44, boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => { setSelecting(v => !v); setSelected(new Set()); }}
            style={{ ...S.textBtn, minHeight: 44 }}>
            {selecting ? 'Готово' : 'Выбрать'}
          </button>
          <button onClick={() => setSheet('filter')}
            style={{ ...S.textBtn, minHeight: 44, display: 'flex', alignItems: 'center', gap: 4 }}>
            {STATUS_LABEL[status]} · {counts[status === 'all' ? 'all' : status] ?? '—'} <ChevronDown size={14} />
          </button>
          <button onClick={() => setSheet('sort')}
            style={{ ...S.textBtn, minHeight: 44, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            {SORT_LABEL[sort]} <ChevronDown size={14} />
          </button>
        </div>
        <div style={SS.consequence}>
          {busy ? 'Спрашиваю сервер…'
            : error ? <span style={{ color: colors.red }}>{error}</span>
            : asOf ? `Обновлено в ${asOf.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : ''}
        </div>
      </div>

      {creds && (
        <div style={{ ...S.settingsCard, padding: 16, margin: '0 16px 12px' }}>
          <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: colors.gray900, marginBottom: 4 }}>
            Пользователь создан
          </div>
          <div style={{ ...SS.consequence, marginBottom: 10 }}>
            Пароль показывается один раз — второго раза не будет.
          </div>
          <div style={{ display: 'grid', gap: 8, fontFamily: 'monospace', fontSize: 15, color: colors.gray900 }}>
            <div>{creds.username}</div>
            <div>{creds.password}</div>
            <div>{creds.sip_extension}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={() => navigator.clipboard?.writeText(
              `Логин: ${creds.username}\nПароль: ${creds.password}\nSIP: ${creds.sip_extension}`)}
              style={{ ...S.primaryBtn, flex: 1 }}>Копировать</button>
            <button onClick={() => setCreds(null)} style={{ ...S.textBtn, minHeight: 44, padding: '0 12px' }}>Готово</button>
          </div>
        </div>
      )}

      <div style={S.settingsCard}>
        {items.map((u, i) => (
          <button key={u.id}
            onClick={() => {
              if (!selecting) { onOpenUser(u.id); return; }
              setSelected(prev => {
                const next = new Set(prev);
                if (next.has(u.id)) next.delete(u.id); else next.add(u.id);
                return next;
              });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 64,
              padding: '10px 16px', background: 'none', cursor: 'pointer', textAlign: 'left',
              border: 'none', borderBottom: i < items.length - 1 ? `1px solid ${colors.gray100}` : 'none',
              fontFamily: FONT,
            }}>
            {selecting && (
              <span style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                border: `2px solid ${selected.has(u.id) ? colors.gray900 : colors.gray400}`,
                background: selected.has(u.id) ? colors.gray900 : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: colors.white, fontSize: 14,
              }}>{selected.has(u.id) ? '✓' : ''}</span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 15, fontWeight: 500,
                color: u.disabled ? colors.gray500 : colors.gray900,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {u.admin_note || u.display_name || '@' + u.username}
                {u.role === 'admin' && (
                  <span style={{ fontSize: 11, color: colors.gray400, marginLeft: 8 }}>АДМИН</span>
                )}
                {u.disabled ? (
                  <span style={{ fontSize: 11, color: colors.red, marginLeft: 8 }}>ЗАБЛОКИРОВАН</span>
                ) : null}
              </div>
              {/* Always the stable identifiers, because the line above is a
                  note somebody typed and may be anything at all. */}
              <div style={{ fontSize: 12.5, color: colors.gray600, fontFamily: 'monospace' }}>
                @{u.username} · {u.sip_extension}
                {u.online ? ' · в сети' : u.last_seen ? '' : ' · ни разу не заходил'}
              </div>
            </div>
            {!selecting && <ChevronRight size={18} color={colors.gray400} />}
          </button>
        ))}
        {items.length === 0 && !busy && (
          <div style={{ padding: 16 }}>
            <div style={{ fontFamily: FONT, fontSize: 15, color: colors.gray900 }}>Никто не найден</div>
            <div style={{ ...SS.consequence, marginTop: 4 }}>
              Логины выдаются случайно — ищите по заметке или по номеру.
            </div>
          </div>
        )}
      </div>

      {cursor && (
        <button onClick={() => load({ cursor })} disabled={busy}
          style={{ ...S.textBtn, width: '100%', minHeight: 48, marginTop: 8 }}>
          Показать ещё 50 · {items.length} из {counts.all ?? '?'}
        </button>
      )}

      {/* Reachable by a thumb, unlike the top-right corner. */}
      <button onClick={create} aria-label="Создать пользователя"
        style={{
          position: 'fixed', right: 20, bottom: 'calc(90px + env(safe-area-inset-bottom))',
          width: 52, height: 52, borderRadius: 26, border: 'none', cursor: 'pointer',
          background: colors.gray900, color: colors.white,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 40,
        }}>
        <Plus size={24} />
      </button>

      {selecting && selected.size > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0,
          bottom: 'calc(76px + env(safe-area-inset-bottom))', zIndex: 45,
          background: colors.white, borderTop: `1px solid ${colors.gray200}`,
          padding: '10px 16px calc(10px + env(safe-area-inset-bottom))',
          display: 'flex', alignItems: 'center', gap: 12, fontFamily: FONT,
        }}>
          <span style={{ fontSize: 14, color: colors.gray600 }}>Выбрано: {selected.size}</span>
          <button disabled={busy} onClick={() => bulk('disable')}
            style={{ ...S.textBtn, marginLeft: 'auto', minHeight: 44, color: colors.red }}>
            Заблокировать
          </button>
          <button disabled={busy} onClick={() => bulk('enable')}
            style={{ ...S.textBtn, minHeight: 44 }}>
            Разблокировать
          </button>
        </div>
      )}

      {undo && (
        <UndoToast
          text={undo.text}
          onAction={async () => {
            const ids = undo.ids;
            setUndo(null);
            await act(() => api.adminBulk('enable', ids));
          }}
          onExpire={() => setUndo(null)}
        />
      )}

      {sheet === 'filter' && (
        <AdminFilterSheet status={status} counts={counts}
          onPick={setStatus} onClose={() => setSheet(null)} />
      )}
      {sheet === 'sort' && (
        <AdminSortSheet sort={sort} onPick={setSort} onClose={() => setSheet(null)} />
      )}
    </SettingsScreen>
  );
}
